import React, { useCallback, useEffect } from 'react';

import styled, { css } from 'styled-components';

import featureNames from '~/shared/constants/featureFlags';
import { promoPriceHiddenPromoTypes } from '~/shared/constants/products';
import { isMysteryBox } from '~/shared/utils/isMysteryBox';
import useEMPPricing from '~/shared/utils/useEMPPricing';
import useHasFeature from '~/shared/utils/useHasFeature';
import useIsEmpMembership from '~/shared/utils/useIsEmpMembership';
import { useMembership } from '~/techstyle-shared/react-accounts';
import {
  Button,
  ProductPrice,
  Tooltip,
  useProductDetailContext,
} from '~/techstyle-shared/react-components';
import { FormattedMessage, useIntl } from '~/techstyle-shared/react-intl';

import errorMessages from './errorMessages';
import {
  useProductDetailCTAContext,
  ProductDetailCTAErrorTypes,
} from './ProductDetailCTAContext';

const Wrapper = styled.div`
  margin-top: 32px;
`;

const EMPAddToBagButton = styled(Button).attrs({
  variant: 'primaryAddToBag',
  size: 'lg',
})`
  width: 100%;
  color: ${({ theme }) => theme.colors.textLight};

  ${({ theme }) => theme.pdpStyles.empAddToBag.button}
`;

const labelStyle = css`
  font-size: 18px;
  font-weight: 800;
  letter-spacing: ${-0.4 / 18}em;
  color: inherit;
`;

const secondaryLabelStyle = css`
  ${labelStyle};
`;

const EMPPrice = styled.span`
  font-weight: 600;
`;

const VIPAddToBagButton = styled(Button).attrs({
  variant: 'primary',
  size: 'lg',
})`
  width: 100%;

  ${({ theme }) => theme.pdpStyles.vipAddToBag.button}
`;

const TextWrapper = styled.div`
  color: ${({ theme }) => theme.colors.vipAddToBagText};
  text-align: center;
  margin: 12px;
  font-size: 14px;
  line-height: 1.71;
  letter-spacing: ${-0.2 / 14}em;
`;

const ByoTextWrapper = styled.div`
  color: ${({ theme }) => theme.colors.focusText};

  font-size: 14px;
  line-height: ${18 / 14};
  text-align: center;

  ${({ theme }) => theme.byo.pdpCtaTooltipTextWrapper};
`;

const promoPriceStyle = css`
  ${({ theme }) => theme.pdpStyles.vipAddToBag.label.promoPrice}
`;

const EMPTooltipContent = styled(Tooltip.Content)`
  font-size: 12px;
  z-index: 1;
  padding: 15px;
  width: 260px;
  && {
    background-color: ${({ theme }) => theme.colors.empTooltipBg};
  }
`;

const BouncebackEndowmentText = styled.div`
  margin-top: 8px;
  text-align: center;
  text-transform: uppercase;
  font-size: 16px;
`;

const BouncebackEndowmentSubtext = styled.div`
  text-align: center;
  font-size: 12px;
  font-style: italic;
  width: 282px;
  margin: auto;
  padding-top: 5px;
`;

const DynamicFormattedMessage = FormattedMessage;

const EMPButton = (props) => {
  const intl = useIntl();
  const productDetailFromCtx = useProductDetailContext();
  const tokenValue = productDetailFromCtx.product.tokenRedemptionValue;
  const {
    addToCart,
    handleSharedValidation,
    onSuccessfulAddToCart,
    setErrorMessage,
    hasError,
  } = useProductDetailCTAContext();

  useEffect(() => {
    if (!hasError) {
      setErrorMessage(null);
    }
  }, [hasError, setErrorMessage]);

  const handleEMPClick = useCallback(async () => {
    try {
      await handleSharedValidation();
      await addToCart({ applyMemberCredit: true });

      onSuccessfulAddToCart && onSuccessfulAddToCart();
    } catch (error) {
      if (error.message === ProductDetailCTAErrorTypes.SelectorError) {
        setErrorMessage(intl.formatMessage(errorMessages.sizeSelectorError));
      }
    }
  }, [
    addToCart,
    handleSharedValidation,
    intl,
    onSuccessfulAddToCart,
    setErrorMessage,
  ]);

  return (
    <EMPAddToBagButton
      variant="primaryAddToBag"
      labelStyle={labelStyle}
      onClick={handleEMPClick}
      data-autotag="pdp-atc-cta-credit"
      data-cnstrc-btn="add-to-cart"
      {...props}
    >
      <FormattedMessage id="site_pdp.add_to_cart" defaultMessage="Add to Bag" />
      <EMPPrice>
        <FormattedMessage
          id="site_pdp.cta_member_credits"
          defaultMessage=" - {count, plural, one {# Member Credit} other {# Member Credits}}"
          values={{ count: tokenValue }}
        />
      </EMPPrice>
    </EMPAddToBagButton>
  );
};

function EMPAddToBag() {
  const productDetailFromCtx = useProductDetailContext();
  const membership = useMembership();
  const { availableTokenQuantity, isVip } = membership;
  const { isTokenOnly, hasTokenValue, tokenValue } = useEMPPricing(
    productDetailFromCtx.product
  );
  const promoTypeId = productDetailFromCtx.product?.promos?.promoTypeId;
  const showPromoPrice = !promoPriceHiddenPromoTypes.has(promoTypeId);

  /**
   * bounceback endowment is currently only supported for Enhanced Membership Program (EMP) users
   * that are using member credits. once this feature is available for non-EMP, bounceback endowment will
   * require usage of `credits` from `useMembershipPerks()` to provide either membership credits or
   * tokens based on the user's membership type.
   */
  const showBouncebackEndowmentMsg =
    productDetailFromCtx.product?.isEndowmentEligible &&
    availableTokenQuantity > 0;

  const isBouncebackEndowmentEnabled = useHasFeature(
    featureNames.BOUNCEBACK_ENDOWMENT
  );

  const {
    addToCart,
    handleSharedValidation,
    isLoading,
    isOutOfStock,
    onSuccessfulAddToCart,
    setErrorMessage,
    hasError,
    isByoQualifiedProduct,
    offerTypeKey,
    offerTypePlainText,
  } = useProductDetailCTAContext();
  const intl = useIntl();
  const vipBtnDisabled = isLoading || isOutOfStock;

  const { isEmpMembership } = useIsEmpMembership();

  useEffect(() => {
    if (!hasError) {
      setErrorMessage(null);
    }
  }, [hasError, setErrorMessage]);

  const onCTAClick = useCallback(async () => {
    try {
      await handleSharedValidation();
      await addToCart();

      onSuccessfulAddToCart && onSuccessfulAddToCart();
    } catch (error) {
      if (error.message === ProductDetailCTAErrorTypes.SelectorError) {
        setErrorMessage(intl.formatMessage(errorMessages.sizeSelectorError));
      }
    }
  }, [
    addToCart,
    handleSharedValidation,
    intl,
    onSuccessfulAddToCart,
    setErrorMessage,
  ]);

  const empBtnDisabled = tokenValue > availableTokenQuantity || vipBtnDisabled;
  const showToolTip = tokenValue > availableTokenQuantity;
  const mysteryBox = isMysteryBox(productDetailFromCtx.product);
  const addWithTokenButtonVisible =
    hasTokenValue && !isByoQualifiedProduct && isEmpMembership && !mysteryBox;

  return (
    <Wrapper>
      {isTokenOnly && (
        <Tooltip isShowing={showToolTip ? undefined : false}>
          <Tooltip.Anchor>
            <EMPButton disabled={empBtnDisabled} />
          </Tooltip.Anchor>
          <EMPTooltipContent>
            <FormattedMessage
              id="site_pdp.notice_cemp_no_credits_message"
              defaultMessage="You don't have enough Member Credits to purchase this item! Make sure you don't skip the next month, and you'll receive one on the 6th!"
            />
          </EMPTooltipContent>
        </Tooltip>
      )}
      {!isTokenOnly && (
        <>
          {addWithTokenButtonVisible && (
            <>
              <Tooltip isShowing={showToolTip ? undefined : false}>
                <Tooltip.Anchor>
                  <EMPButton disabled={empBtnDisabled} />
                </Tooltip.Anchor>
                <EMPTooltipContent>
                  <FormattedMessage
                    id="site_pdp.notice_cemp_no_credits_message"
                    defaultMessage="You don't have enough Member Credits to purchase this item! Make sure you don't skip the next month, and you'll receive one on the 6th!"
                  />
                </EMPTooltipContent>
              </Tooltip>
              {isBouncebackEndowmentEnabled && showBouncebackEndowmentMsg && (
                <>
                  <BouncebackEndowmentText>
                    <FormattedMessage
                      id="site_pdp.bounceback_endowment_atb"
                      defaultMessage="BUY WITH VIP CREDIT + GET FREE GIFT CARD"
                    />
                  </BouncebackEndowmentText>
                  <BouncebackEndowmentSubtext>
                    <FormattedMessage
                      id="site_pdp.bounceback_endowment_atb_disclaimer"
                      defaultMessage="One time use. Shopping Credits expire in 90 days. Some restrictions apply."
                    />
                  </BouncebackEndowmentSubtext>
                </>
              )}
              <TextWrapper>
                <FormattedMessage
                  id="global_cta.outfit_credit_or"
                  defaultMessage="or"
                />
              </TextWrapper>
            </>
          )}
          <VIPAddToBagButton
            hasTokenValue={hasTokenValue}
            variant="secondary"
            data-autotag="pdp-add-to-cart"
            data-cnstrc-btn="add-to-cart"
            labelStyle={secondaryLabelStyle}
            disabled={vipBtnDisabled}
            onClick={onCTAClick}
          >
            {isOutOfStock ? (
              <FormattedMessage
                id="global_cta.out_of_stock"
                defaultMessage="Out of Stock"
              />
            ) : (
              <FormattedMessage
                id="site_pdp.add_to_cart"
                defaultMessage="Add to Bag"
              />
            )}
            {hasTokenValue && (
              <EMPPrice>
                {' - '}
                <ProductPrice
                  promoPriceStyle={promoPriceStyle}
                  showPromoPrice={showPromoPrice}
                  showVipPrice={!showPromoPrice}
                />
              </EMPPrice>
            )}
          </VIPAddToBagButton>
          {isVip && isByoQualifiedProduct && (
            <ByoTextWrapper>
              <DynamicFormattedMessage
                id={`byob.${offerTypeKey}_cta_tooltip`}
                defaultMessage={`or ${offerTypePlainText} Member Credit`}
              />
            </ByoTextWrapper>
          )}
        </>
      )}
    </Wrapper>
  );
}

export default EMPAddToBag;
